/**
 * Gibbs conversation history — localStorage-backed.
 *
 * Tracks the last 50 user interactions for session continuity.
 * Entries are stored as a JSON array in localStorage under
 * `gibbs.conversationHistory`.
 *
 * @module gibbs/conversationHistory
 */

/** localStorage key for conversation history. */
const STORAGE_KEY = 'gibbs.conversationHistory';

/** Maximum number of history entries to retain. */
const MAX_ENTRIES = 50;

/** Default number of recent entries returned by getRecentEntries. */
const DEFAULT_RECENT_COUNT = 5;

/**
 * Maximum length of stored assistant response text (chars). Long enough
 * to give the LLM a usable referent for cross-turn pronoun resolution
 * without bloating prompt tokens. ~200 chars is roughly 50 tokens per
 * entry; 5 entries cap at ~250 tokens of assistant text.
 */
const ASSISTANT_CONTENT_MAX = 200;

/**
 * @typedef {Object} HistoryEntry
 * @property {string} timestamp - ISO 8601 timestamp
 * @property {string} userMessage - The user's input text
 * @property {string} responseType - One of: plan, chat, clarification, workflow
 * @property {string[]} actionIds - Action IDs involved in the response
 * @property {string} [assistantContent] - Truncated copy of Gibbs's reply
 *   text. Optional so legacy entries (pre-2026-04-28) keep working.
 */

/**
 * Truncate a string to the assistant-content cap, with ellipsis if cut.
 * Returns empty string for non-strings (defensive — JSX, undefined, etc.).
 *
 * @param {string} s
 * @returns {string}
 * @private
 */
function truncateAssistantContent(s) {
    if (typeof s !== 'string' || s.length === 0) return '';
    if (s.length <= ASSISTANT_CONTENT_MAX) return s;
    return `${s.slice(0, ASSISTANT_CONTENT_MAX - 1)}…`;
}

/**
 * Read the full history array from localStorage.
 *
 * @returns {HistoryEntry[]}
 * @private
 */
function readStorage() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch (_) {
        return [];
    }
}

/**
 * Write the history array to localStorage.
 *
 * @param {HistoryEntry[]} entries
 * @private
 */
function writeStorage(entries) {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
    } catch (err) {
        console.warn('[gibbs/conversationHistory] Failed to write localStorage:', err.message);
    }
}

/**
 * Add a conversation entry to the history.
 *
 * Evicts the oldest entry if the history has reached the 50-entry cap.
 *
 * @param {string} userMessage - The user's input text.
 * @param {string} responseType - Response type (plan, chat, clarification, workflow).
 * @param {string[]} [actionIds=[]] - Action IDs used in the response.
 * @param {string} [assistantContent] - Truncated assistant reply text.
 *   Recommended for `chat` and `clarification` responseTypes so future
 *   turns can resolve pronouns ("it", "that") against what Gibbs said.
 *   For `plan`/`workflow` responses, pass the workflow summary string.
 *   Truncated to ASSISTANT_CONTENT_MAX chars; non-string input is
 *   coerced to empty string.
 * @returns {HistoryEntry} The entry that was added.
 */
export function addEntry(userMessage, responseType, actionIds = [], assistantContent = '') {
    const entry = {
        timestamp: new Date().toISOString(),
        userMessage,
        responseType,
        actionIds,
    };

    const trimmed = truncateAssistantContent(assistantContent);
    if (trimmed) entry.assistantContent = trimmed;

    const entries = readStorage();
    entries.push(entry);

    // Evict oldest entries beyond the cap.
    while (entries.length > MAX_ENTRIES) {
        entries.shift();
    }

    writeStorage(entries);
    return entry;
}

/**
 * Get the most recent N history entries.
 *
 * @param {number} [count=5] - Number of entries to return.
 * @returns {HistoryEntry[]} Most recent entries, newest last.
 */
export function getRecentEntries(count = DEFAULT_RECENT_COUNT) {
    const entries = readStorage();
    if (count >= entries.length) return entries;
    return entries.slice(-count);
}

/**
 * Serialize recent history into a compact context string for LLM prompts.
 *
 * Produces ~300 tokens for 5 entries.
 *
 * @param {number} [count=5] - Number of recent entries to include.
 * @returns {string}
 */
export function serializeHistoryContext(count = DEFAULT_RECENT_COUNT) {
    const entries = getRecentEntries(count);
    if (entries.length === 0) return '';

    const lines = ['Recent Interactions:'];

    entries.forEach((entry, i) => {
        const time = formatTime(entry.timestamp);
        const actions = entry.actionIds.length > 0
            ? ` (${entry.actionIds.join(', ')})`
            : '';
        // Surface assistant content when present so cross-turn pronoun
        // references ("it", "that") can resolve against what Gibbs
        // actually said, not just the user's prior message + the
        // response-type label.
        const replyExcerpt = entry.assistantContent
            ? `: ${entry.assistantContent}`
            : '';
        lines.push(`${i + 1}. [${time}] "${entry.userMessage}" \u2192 ${entry.responseType}${replyExcerpt}${actions}`);
    });

    return lines.join('\n');
}

/**
 * Format an ISO timestamp to a short HH:MM string.
 *
 * @param {string} isoString
 * @returns {string}
 * @private
 */
function formatTime(isoString) {
    try {
        const d = new Date(isoString);
        const hh = String(d.getHours()).padStart(2, '0');
        const mm = String(d.getMinutes()).padStart(2, '0');
        return `${hh}:${mm}`;
    } catch (_) {
        return '??:??';
    }
}

/**
 * Clear all conversation history from localStorage.
 */
export function clearHistory() {
    try {
        localStorage.removeItem(STORAGE_KEY);
    } catch (err) {
        console.warn('[gibbs/conversationHistory] Failed to clear localStorage:', err.message);
    }
}

/**
 * Export the full conversation history as a JSON string.
 *
 * @returns {string} JSON string of all history entries.
 */
export function exportHistory() {
    const entries = readStorage();
    return JSON.stringify(entries, null, 2);
}

/**
 * Get the total number of stored entries.
 *
 * @returns {number}
 */
export function getEntryCount() {
    return readStorage().length;
}
